import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Loader2, Edit, UserPlus, KeyRound, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import PartyForm from '../components/PartyForm';
import ClientUserDialog from '../components/ClientUserDialog';
import PartyStatement from '../components/PartyStatement';
import AssignUserToPartyModal from '../components/AssignUserToPartyModal';
import PartySubscriptionsSection from '../components/PartySubscriptionsSection';
import { DocumentAttachments } from '@/app/components/DocumentAttachments';
import { ClickableInvoiceNumber } from '../components/ClickableInvoiceNumber';
import TaskList from '@/app/components/tasks/TaskList';
import TaskForm from '@/app/components/tasks/TaskForm';
import { Task } from '@/app/types/task';
import {
  getParty,
  updateParty,
  deleteParty,
  createClientUser,
  resetClientPin,
  listPartyGroups,
  assignUserToParty,
  unassignUserFromParty,
  getPartyUsers
} from '@/app/services/partyService';
import { listVouchers } from '@/app/services/voucherService';
import { listOutstandingInvoices } from '@/app/services/paymentService';
import { 
  fetchOutstandingReceivables,
  fetchPartyClosingBalance
} from '@/app/services/reportService';
import { listSites } from '@/app/services/siteService';
import { usePreferenceStore } from '@/app/store/preferenceStore';
import { format } from 'date-fns';
import { Party, PartyUpdate } from '@/app/types/party';
import { Site } from '@/app/types/site';
import { ListQueryParams } from '@/app/types/api';
import { useOrganization } from '@/app/hooks/useOrganization';

export default function PartyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();
  const { defaultSiteId } = usePreferenceStore();
  const [activeTab, setActiveTab] = useState('info');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showClientUserDialog, setShowClientUserDialog] = useState(false);
  const [clientUserDialogMode, setClientUserDialogMode] = useState<'create' | 'reset'>('create');
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);

  const partyId = id ? parseInt(id, 10) : null;

  // Fetch party data
  const { data: party, isLoading: isLoadingParty, error: partyError } = useQuery({
    queryKey: ['party', partyId],
    queryFn: () => getParty(partyId!),
    enabled: Boolean(partyId),
  });

  // Fetch party groups
  const { data: partyGroupsData } = useQuery({
    queryKey: ['partyGroups', currentOrganizationId],
    queryFn: () => listPartyGroups({ limit: 100 }, currentOrganizationId!),
    enabled: !!currentOrganizationId,
  });

  // Fetch sites
  const { data: sitesData } = useQuery({
    queryKey: ['sites', currentOrganizationId],
    queryFn: () => listSites({ limit: 100, show_all: true }, currentOrganizationId!),
    enabled: !!currentOrganizationId,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: PartyUpdate) => updateParty(partyId!, data),
    onSuccess: () => {
      toast.success('Party updated successfully');
      queryClient.invalidateQueries({ queryKey: ['parties'] });
      queryClient.invalidateQueries({ queryKey: ['party', partyId] });
      setIsEditMode(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update party');
    },
  });

  // User assignment handlers
  const handleAssignUsers = async (userIds: string[]) => {
    if (!partyId) return;

    try {
      // Assign all selected users
      await Promise.all(
        userIds.map(userId => assignUserToParty(partyId, parseInt(userId)))
      );

      toast.success(`Successfully assigned ${userIds.length} user(s) to party`);
      queryClient.invalidateQueries({ queryKey: ['party-users', partyId] });
      queryClient.invalidateQueries({ queryKey: ['organizationUsers'] });
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to assign users');
      throw error;
    }
  };

  const handleUnassignUser = async (userId: number) => {
    if (!partyId) return;

    if (!confirm('Are you sure you want to remove this user\'s access to this party?')) {
      return;
    }

    try {
      await unassignUserFromParty(partyId, userId);
      toast.success('User unassigned successfully');
      queryClient.invalidateQueries({ queryKey: ['party-users', partyId] });
      queryClient.invalidateQueries({ queryKey: ['organizationUsers'] });
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to unassign user');
    }
  };

  // Client user mutations are handled inside the dialog

  const handleEdit = () => {
    setIsEditMode(true);
    setActiveTab('info');
  };


  const handleCreateClientUser = () => {
    setClientUserDialogMode('create');
    setShowClientUserDialog(true);
  };

  const handleResetClientPin = () => {
    setClientUserDialogMode('reset');
    setShowClientUserDialog(true);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoadingParty) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (partyError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load party: {(partyError as any).message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Parties
          </Button>
          <h1 className="text-xl font-semibold tracking-tight">
            {party?.name}
          </h1>
          {party?.party_group && (
            <Badge 
              variant="outline" 
              className="cursor-pointer hover:bg-secondary transition-colors"
              onClick={() => navigate(`/party?group_id=${party.party_group.id}`)}
            >
              {party.party_group.name}
            </Badge>
          )}
          {party?.site && (
            <Badge 
              variant="secondary" 
              className="cursor-pointer hover:bg-secondary transition-colors"
              onClick={() => navigate(`/party?site_id=${party.site.id}`)}
            >
              {party.site.name}
            </Badge>
          )}
        </div>

        {/* Actions */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              Actions
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleEdit}>
              <Edit className="mr-2 h-4 w-4" />
              <span>Edit</span>
            </DropdownMenuItem>
            
            {/* Client User Actions */}
            {party?.phone && (
              <>
                <DropdownMenuSeparator />
                {!party.client_user_id ? (
                  <DropdownMenuItem onClick={handleCreateClientUser}>
                    <UserPlus className="mr-2 h-4 w-4" />
                    <span>Create Client User</span>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={handleResetClientPin}>
                    <KeyRound className="mr-2 h-4 w-4" />
                    <span>Reset Client PIN</span>
                  </DropdownMenuItem>
                )}
              </>
            )}
            
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Main Content with Tabs */}
      <Card>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-9">
              <TabsTrigger value="info">Party Information</TabsTrigger>
              <TabsTrigger value="statement">Statement</TabsTrigger>
              <TabsTrigger value="sales">Sales</TabsTrigger>
              <TabsTrigger value="payments">Payments</TabsTrigger>
              <TabsTrigger value="outstanding">Outstanding</TabsTrigger>
              <TabsTrigger value="purchases">Purchases</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="tasks">Tasks</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-4 mt-6">
              <PartyInfoTab party={party!} />
            </TabsContent>

            <TabsContent value="statement" className="space-y-4 mt-6">
              <PartyStatement partyId={partyId!} partyName={party?.name} />
            </TabsContent>

            <TabsContent value="sales" className="space-y-4 mt-6">
              <SalesTab partyId={partyId!} />
            </TabsContent>

            <TabsContent value="payments" className="space-y-4 mt-6">
              <PaymentsTab partyId={partyId!} />
            </TabsContent>

            <TabsContent value="outstanding" className="space-y-4 mt-6">
              <OutstandingTab partyId={partyId!} />
            </TabsContent>

            <TabsContent value="purchases" className="space-y-4 mt-6">
              <PurchasesTab partyId={partyId!} />
            </TabsContent>

            <TabsContent value="users" className="space-y-4 mt-6">
              <PartyUsersTab
                partyId={partyId!}
                onAssignUser={() => setShowAssignUserModal(true)}
                onUnassignUser={handleUnassignUser}
              />
            </TabsContent>

            <TabsContent value="tasks" className="space-y-4 mt-6">
              <TasksTab partyId={partyId!} />
            </TabsContent>

            <TabsContent value="documents" className="space-y-4 mt-6">
              <DocumentAttachments
                entityType="party"
                entityId={partyId!}
                title="Party Documents"
                maxFileSize={10}
                maxFiles={20}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Client User Dialog */}
      <ClientUserDialog
        open={showClientUserDialog}
        onOpenChange={setShowClientUserDialog}
        party={party}
        mode={clientUserDialogMode}
      />

      {/* Assign User to Party Modal */}
      {party && currentOrganizationId && (
        <AssignUserToPartyModal
          open={showAssignUserModal}
          onOpenChange={setShowAssignUserModal}
          party={party}
          onAssignUsers={handleAssignUsers}
          organizationId={currentOrganizationId}
        />
      )}

      {/* Edit Party Form Dialog */}
      {party && (
        <PartyForm
          open={isEditMode}
          onOpenChange={(open) => {
            setIsEditMode(open);
          }}
          onSubmit={(data) => updateMutation.mutate(data as PartyUpdate)}
          defaultValues={party}
          isLoading={updateMutation.isPending}
          mode="edit"
          partyGroups={partyGroupsData?.items || []}
          sites={sitesData?.items || []}
          defaultSiteId={defaultSiteId}
        />
      )}
    </div>
  );
}

// Click to copy helper component
function ClickToCopy({ value, label }: { value: string | null | undefined; label: string }) {
  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(value);
      toast.success(`${label} copied to clipboard`);
    }
  };

  if (!value) return <span>-</span>;

  return (
    <div className="flex items-center gap-1 group">
      <span className="font-medium">{value}</span>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleCopy}
      >
        <Copy className="h-3 w-3" />
      </Button>
    </div>
  );
}

// Party Info Tab Component
function PartyInfoTab({ party }: { party: Party }) {
  const today = new Date();
  const yearEnd = new Date(today.getFullYear(), 11, 31); // December 31st of current year

  // Fetch party closing balance
  const { data: closingBalance } = useQuery({
    queryKey: ['party-closing-balance', party.id],
    queryFn: () => fetchPartyClosingBalance({
      party_id: party.id,
      start_date: '1970-01-01', // Beginning of time to include all transactions
      end_date: format(yearEnd, 'yyyy-MM-dd'), // Year end
    }),
    enabled: Boolean(party.id),
  });

  return (
    <div className="space-y-6">
      {/* Compliance Subscriptions Section */}
      <PartySubscriptionsSection partyId={party.id} partyName={party.name} />
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Name</span>
                <span className="font-medium">{party.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Phone</span>
                <ClickToCopy value={party.phone} label="Phone number" />
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Email</span>
                <ClickToCopy value={party.email} label="Email" />
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Party Type</span>
                <span className="font-medium">{party.primary_party_type || 'Customer'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Party Group</span>
                <span className="font-medium">
                  {party.party_group ? (
                    <Badge variant="outline">{party.party_group.name}</Badge>
                  ) : (
                    '-'
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Site</span>
                <span className="font-medium">
                  {party.site ? (
                    <Badge variant="secondary">{party.site.name}</Badge>
                  ) : (
                    '-'
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Responsible Party</span>
                <span className="font-medium">
                  {party.responsible_party ? (
                    <Badge variant="outline">{party.responsible_party.name}</Badge>
                  ) : (
                    '-'
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Source</span>
                <span className="font-medium">
                  {party.source ? (
                    <Badge variant="outline">{party.source}</Badge>
                  ) : (
                    '-'
                  )}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4">GST Information</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">GSTIN</span>
                <ClickToCopy value={party.gstin} label="GSTIN" />
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">GST Type</span>
                <span className="font-medium">{party.gst_type || 'Not Specified'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Address and Credit Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4">Address Details</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Billing Address</p>
                <p className="text-sm">{party.billing_address || '-'}</p>
              </div>
              {party.has_separate_shipping && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Shipping Address</p>
                  <p className="text-sm">{party.shipping_address || '-'}</p>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">State</span>
                <span className="font-medium">{party.state || '-'}</span>
              </div>
              {party.geo_location && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Geographic Location</span>
                  <ClickToCopy value={party.geo_location} label="Geographic location" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4">Credit Information</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Credit Limit Type</span>
                <span className="font-medium">{party.credit_limit_type === 'no_limit' ? 'No Limit' : 'Custom Limit'}</span>
              </div>
              {party.credit_limit_type === 'custom_limit' && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Credit Limit Amount</span>
                  <span className="font-medium">₹{party.credit_limit_amount ? parseFloat(party.credit_limit_amount.toString()).toFixed(2) : '0.00'}</span>
                </div>
              )}
              {closingBalance && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Current Balance</span>
                  <span className="font-medium">
                    ₹{parseFloat(closingBalance.closing_balance).toFixed(2)} ({closingBalance.closing_balance_type})
                  </span>
                </div>
              )}
              {party.unallocated_credit && Number(party.unallocated_credit) > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Unallocated Credit</span>
                  <span className="font-medium text-warning">
                    ₹{parseFloat(party.unallocated_credit.toString()).toFixed(2)}
                  </span>
                </div>
              )}
              {party.unallocated_debit && Number(party.unallocated_debit) > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Unallocated Debit</span>
                  <span className="font-medium text-warning">
                    ₹{parseFloat(party.unallocated_debit.toString()).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Sales Tab Component
function SalesTab({ partyId }: { partyId: number }) {
  const navigate = useNavigate();
  
  // Fetch sales invoices
  const { data: salesData, isLoading } = useQuery({
    queryKey: ['party-sales', partyId],
    queryFn: () => listVouchers({
      filters: JSON.stringify({
        party_id: partyId,
        voucher_type_id: 1 // SINV - Sales Invoice
      }),
      limit: 50,
      sort_by: 'transaction_date',
      sort_dir: 'desc'
    }),
    enabled: Boolean(partyId),
  });

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const invoices = salesData?.items || [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Sales Invoices</h3>
        <Button 
          size="sm" 
          onClick={() => navigate(`/sales-invoices/detail?party_id=${partyId}`)}
        >
          New Sales Invoice
        </Button>
      </div>

      {invoices.length > 0 ? (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Invoice No</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <ClickableInvoiceNumber 
                      invoiceNumber={invoice.voucher_number} 
                      invoiceId={invoice.id} 
                      type="sales" 
                    />
                  </td>
                  <td className="px-4 py-3 text-sm">{new Date(invoice.transaction_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm">₹{parseFloat(invoice.total_amount || '0').toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm">
                    <Badge variant={invoice.status === 'posted' ? 'default' : 'secondary'}>
                      {invoice.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate(`/sales-invoices/detail/${invoice.id}`)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Alert>
          <AlertDescription>No sales invoices found for this party.</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

// Purchases Tab Component
function PurchasesTab({ partyId }: { partyId: number }) {
  const navigate = useNavigate();
  
  // Fetch purchase invoices
  const { data: purchaseData, isLoading } = useQuery({
    queryKey: ['party-purchases', partyId],
    queryFn: () => listVouchers({
      filters: JSON.stringify({
        party_id: partyId,
        voucher_type_id: 2 // PINV - Purchase Invoice
      }),
      limit: 50,
      sort_by: 'transaction_date',
      sort_dir: 'desc'
    }),
    enabled: Boolean(partyId),
  });

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const invoices = purchaseData?.items || [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Purchase Invoices</h3>
        <Button 
          size="sm" 
          onClick={() => navigate(`/purchase-invoices/detail?party_id=${partyId}`)}
        >
          New Purchase Invoice
        </Button>
      </div>

      {invoices.length > 0 ? (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Invoice No</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <ClickableInvoiceNumber 
                      invoiceNumber={invoice.voucher_number} 
                      invoiceId={invoice.id} 
                      type="purchase" 
                    />
                  </td>
                  <td className="px-4 py-3 text-sm">{new Date(invoice.transaction_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm">₹{parseFloat(invoice.total_amount || '0').toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm">
                    <Badge variant={invoice.status === 'posted' ? 'default' : 'secondary'}>
                      {invoice.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate(`/purchase-invoices/detail/${invoice.id}`)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Alert>
          <AlertDescription>No purchase invoices found for this party.</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

// Payments Tab Component
function PaymentsTab({ partyId }: { partyId: number }) {
  const navigate = useNavigate();
  
  // Fetch payment receipts
  const { data: paymentData, isLoading } = useQuery({
    queryKey: ['party-payments', partyId],
    queryFn: () => listVouchers({
      filters: JSON.stringify({
        party_id: partyId,
        voucher_type_id: 4 // RCT - Payment Received
      }),
      limit: 50,
      sort_by: 'transaction_date',
      sort_dir: 'desc'
    }),
    enabled: Boolean(partyId),
  });

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const payments = paymentData?.items || [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Payment Receipts</h3>
        <Button 
          size="sm" 
          onClick={() => navigate(`/payments-in?party_id=${partyId}`)}
        >
          New Payment Receipt
        </Button>
      </div>

      {payments.length > 0 ? (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Receipt No</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">
                    <ClickableInvoiceNumber 
                      invoiceNumber={payment.voucher_number} 
                      invoiceId={payment.id} 
                      type="payment" 
                    />
                  </td>
                  <td className="px-4 py-3 text-sm">{new Date(payment.transaction_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm">₹{parseFloat(payment.total_amount || '0').toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm">
                    <Badge variant={payment.status === 'posted' ? 'default' : 'secondary'}>
                      {payment.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => navigate(`/payments-in/detail/${payment.id}`)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Alert>
          <AlertDescription>No payment receipts found for this party.</AlertDescription>
        </Alert>
      )}
    </div>
  );
}

// Tasks Tab Component
function TasksTab({ partyId }: { partyId: number }) {
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const handleCreateTask = () => {
    setSelectedTask(null);
    setShowTaskForm(true);
  };

  const handleEditTask = (task: Task) => {
    setSelectedTask(task);
    setShowTaskForm(true);
  };

  return (
    <>
      <TaskList
        entityType="party"
        entityId={partyId}
        showCreateButton={true}
        onCreateClick={handleCreateTask}
        onTaskClick={handleEditTask}
        onTaskEdit={handleEditTask}
        viewMode="table"
      />
      
      <TaskForm
        open={showTaskForm}
        onOpenChange={setShowTaskForm}
        task={selectedTask}
        parentEntity={{ type: 'party', id: partyId }}
        onSuccess={() => {
          setShowTaskForm(false);
          setSelectedTask(null);
        }}
      />
    </>
  );
}

// Party Users Tab Component
function PartyUsersTab({
  partyId,
  onAssignUser,
  onUnassignUser,
}: {
  partyId: number;
  onAssignUser: () => void;
  onUnassignUser: (userId: number) => void;
}) {
  // Lazy load users only when this tab is active
  const { data: managingUsers, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['party-users', partyId],
    queryFn: () => getPartyUsers(partyId),
    enabled: Boolean(partyId),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Users with Access</h3>
          <p className="text-sm text-muted-foreground">
            Manage which users can access this party account
          </p>
        </div>
        <Button size="sm" onClick={onAssignUser}>
          <UserPlus className="mr-2 h-4 w-4" />
          Assign User
        </Button>
      </div>

      {isLoadingUsers ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : managingUsers && managingUsers.length > 0 ? (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Username</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Full Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {managingUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{user.username}</td>
                  <td className="px-4 py-3 text-sm">{user.full_name || '-'}</td>
                  <td className="px-4 py-3 text-sm">{user.email || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <Badge variant={user.is_active ? 'default' : 'secondary'}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onUnassignUser(user.id)}
                    >
                      Remove Access
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <Alert>
          <AlertDescription>
            No users have access to this party yet. Click "Assign User" to give users access.
          </AlertDescription>
        </Alert>
      )}

      <Alert className="border-primary/30 bg-primary/5">
        <AlertDescription className="text-primary text-sm">
          <strong>Multi-Party Feature:</strong> Users can access multiple parties using the same login credentials.
          Assigning a user here gives them access to this party's data without creating a new login.
        </AlertDescription>
      </Alert>
    </div>
  );
}

// Outstanding Tab Component
function OutstandingTab({ partyId }: { partyId: number }) {
  const navigate = useNavigate();
  
  // Fetch outstanding invoices
  const { data: outstandingData, isLoading } = useQuery({
    queryKey: ['party-outstanding', partyId],
    queryFn: () => listOutstandingInvoices(partyId),
    enabled: Boolean(partyId),
  });

  // Fetch receivables summary
  const { data: receivablesData } = useQuery({
    queryKey: ['party-receivables', partyId],
    queryFn: () => fetchOutstandingReceivables({ party_id: partyId }),
    enabled: Boolean(partyId),
  });

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const invoices = outstandingData || [];
  const partyReceivable = receivablesData?.items?.find((item: any) => item.party_id === partyId);

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      {partyReceivable && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-xl font-semibold tracking-tight">₹{parseFloat(partyReceivable.total_amount || '0').toFixed(2)}</div>
              <p className="text-xs text-muted-foreground">Total Outstanding</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-xl font-semibold tracking-tight">{partyReceivable.invoice_count || 0}</div>
              <p className="text-xs text-muted-foreground">Outstanding Invoices</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-xl font-semibold tracking-tight">{partyReceivable.overdue_days || 0} days</div>
              <p className="text-xs text-muted-foreground">Average Overdue</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Outstanding Invoices Table */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Outstanding Invoices</h3>
        
        {invoices.length > 0 ? (
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Invoice No</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Total Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Paid Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Outstanding</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {invoices.map((invoice: any) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <ClickableInvoiceNumber 
                        invoiceNumber={invoice.voucher_number} 
                        invoiceId={invoice.id} 
                        type="sales" 
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">{new Date(invoice.transaction_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm">₹{parseFloat(invoice.total_amount || '0').toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm">₹{parseFloat(invoice.paid_amount || '0').toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm font-medium">
                      ₹{parseFloat(invoice.outstanding_amount || '0').toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm space-x-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/sales-invoices/detail/${invoice.id}`)}
                      >
                        View
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/payments-in?party_id=${partyId}`)}
                      >
                        Receive Payment
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Alert>
            <AlertDescription>No outstanding invoices for this party.</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}